// This is a mixin that is used to fetch ticket price with booking fee
// It is used in the select user component to fetch the price of the tickets
// and the booking fee so that we can display the total price to the user.
// It is also used in the LegendSeats component to fetch the price of the tickets
// so that we can display the price of the tickets in the legend.

import OrderItem from 'src/models/order-item/OrderItem'
export default {
  data () {
    return {
      userOrderDatas: [],
      transientOrder: null,
      transientOrderItems: {
        member: {},
        packages: [],
        tickets: []
      },

      transientOrderCurrentJobId: 0
    }
  },
  unmounted () {
    _.each(_.flatten(this.userOrderDatas), oi => oi.destroy())
    // Here we update the current job id, in case recomputation in progress, this will trigger destroy
    this.transientOrderCurrentJobId++
    this.resetTransientOrder()
  },
  computed: {
    // Override computed for more complex current showing logic
    // Used to prevent unexpected displays when data is loading / stale data is present
    currentShowing () {
      return this.showing
    },
    packages () {
      return this.currentShowing?.packages('guest') ?? []
    },
    ticketTypes () {
      return this.currentShowing.ticketTypes('guest')
    },
    ticketOrderItems () {
      if (this.transientOrderCurrentJobId === null) return

      return this.transientOrderItems.tickets
    },
    packageOrderItems () {
      if (this.transientOrderCurrentJobId === null) return

      return this.transientOrderItems.packages
    }
  },
  methods: {
    refreshTransientOrder (resetOrder = false) {
      // if some thing is loading, it's going to loaded soon which will cause a recompute so don't bother with recompute now
      if (this.showing.loading || this.user?.loading || this.order?.loading) return

      if (resetOrder) {
        this.resetTransientOrder()
      }

      const transientOrderCurrentJobId = ++this.transientOrderCurrentJobId

      let transientOrder = this.transientOrder
      const transientOrderItems = {
        packages: [],
        tickets: [],
        member: this.transientOrderItems.member
      }

      if (!transientOrder) {
        // We don't have an existing transient order - we need to create one.

        // We need to reset member tickets as the user may have changed.
        transientOrderItems.member = {}
        // Copy order only when transient order doesn't exist as this is compute heavy.
        transientOrder = this.order?.copy(true)
        // Set copied order to transient to safeguard against unwanted operations.
        transientOrder.transient = true

        // Add all tickets types that exist for this showing to transient order
        _.each(this.ticketTypes, ticketType => {
          transientOrder.orderItems.add(new OrderItem({
            member: { name: 'Your Guest' },
            type: 'ticket',
            ticket: {
              showing: this.showing,
              ticketTypeId: ticketType.id
            },
            transient: true
          }))
        })

        // Add all packages that exist for this showing to transient order
        _.each(this.packages, pack => {
          transientOrder.addItemPackage(pack, this.showing, null, true)
        })

        // Add all member tickets that exist for this showing to transient order
        _.each(this.userOrderDatas, userOrderDatas => {
          const userOrderData = userOrderDatas[0]
          const member = userOrderData.ticket.user
          transientOrderItems.member[member.id] = { packages: [], tickets: [] }

          if (userOrderData.itemPackage) {
            transientOrder.addItemPackage(userOrderData.itemPackage, this.showing, member, true)
          } else {
            transientOrder.orderItems.add(new OrderItem({
              member,
              type: 'ticket',
              ticket: {
                user: member,
                showing: this.showing,
                ticketTypeId: userOrderData.ticket.ticketTypeId
              },
              transient: true
            }))
          }
        })
      }

      // We need to work out if any order items have been removed from the real order and also remove these from the transient order
      const nonTransientOrderItems = _.filter(transientOrder?.orderItems?.data, transientOrderItem => transientOrderItem.transient === false && transientOrderItem.options.copiedFromId)
      const itemsToRemoveIds = _.difference(_.map(nonTransientOrderItems, oi => oi.options.copiedFromId) || [], _.map(this.order?.orderItems?.data, 'id') || [])
      const itemsToRemove = _.filter(transientOrder?.orderItems?.data, oi => _.includes(itemsToRemoveIds, oi.options.copiedFromId))
      _.each(itemsToRemove, item => {
        transientOrder.orderItems.remove(item)
        item.destroy()
      })
      // We need to work out if any order items have been added to the real order so we can also add these to the transient order
      const itemsToAddIds = _.difference(_.map(this.order?.orderItems?.data, 'id') || [], _.map(transientOrder?.orderItems?.data, oi => oi.options.copiedFromId || oi.id) || [])
      const itemsToAdd = _.filter(this.order?.orderItems?.data, oi => _.includes(itemsToAddIds, oi.id))
      _.each(itemsToAdd, item => {
        const copiedItem = item.copy(true)
        const tempOrder = copiedItem.data.order
        copiedItem.data.order = null
        transientOrder.orderItems.add(copiedItem)
        if (tempOrder !== this.order) {
          tempOrder?.destroy()
        }
      })

      // Reset discounts
      // Re add discounts
      _.each(transientOrder.orderItems.data, transientOrderItem => {
        transientOrderItem.discount = null

        if (transientOrderItem.transient) return

        const matchingItem = _.find(this.order.orderItems.data, oi => oi.id === transientOrderItem.options.copiedFromId || oi.id === transientOrderItem.id)

        if (!matchingItem) return

        transientOrderItem.discountUserId = matchingItem.discountUserId

        transientOrderItem.discountGroupKey = matchingItem.discountGroupKey

        if (matchingItem.discount === transientOrderItem.discount) return

        transientOrderItem.discountId = matchingItem.discountId
      })

      // Call apply transient redemptions so that discounts/vouchers are applied before we store the price.
      transientOrder.setupCurrentMemberDiscounts()
      transientOrder.applyTransientRedemptions()

      // Iterate over each order item so that we can add these transientOrderItems which are used to fetch pricing.
      _.each(transientOrder.orderItems.data, orderItem => {
        if (!orderItem.transient) return

        if (orderItem.itemPackage) {
          if (orderItem.ticket?.user || orderItem.itemOrderItem?.user) {
            if (!_.includes(transientOrderItems.member[(orderItem.ticket?.user || orderItem.itemOrderItem?.user)?.id]?.packages, orderItem)) {
              transientOrderItems.member[(orderItem.ticket?.user || orderItem.itemOrderItem?.user)?.id]?.packages.push(orderItem)
            }
          } else {
            transientOrderItems.packages.push(orderItem)
          }
        } else if (orderItem.ticket) {
          if (orderItem.ticket?.user) {
            if (!_.includes(transientOrderItems.member[orderItem.ticket?.user.id].tickets, orderItem)) {
              transientOrderItems.member[orderItem.ticket?.user.id].tickets.push(orderItem)
            }
          } else {
            transientOrderItems.tickets.push(orderItem)
          }
        }
      })
      // Ensure no recompute has started since - e.g. another recompute started post our execution
      if (transientOrderCurrentJobId === this.transientOrderCurrentJobId) {
        this.transientOrder = transientOrder
        this.transientOrderItems = transientOrderItems
      } else {
        transientOrder?.destroy()
      }
    },
    resetTransientOrder () {
      // Set transient order to null, as we want to recompute user tickets
      this.transientOrder?.destroy()
      this.transientOrder = null
    }
  }
}
